// Authentication Service
import { getJson } from './apiClient';
import { NetworkError, TimeoutError, AuthenticationError } from '../utils/errors';
import { devLog } from '../utils/devLog';

class AuthService {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    // 프록시 설정을 사용하므로 개발 환경에서는 상대 경로, 프로덕션에서는 절대 경로 사용
    this.baseUrl = this.isDevelopment ? '' : (process.env.REACT_APP_API_URL || 'https://api.astrantis.net');
  }

  // 사용자 정보 저장
  saveUserData(userData) {
    if (userData.email) localStorage.setItem('email', userData.email);
    if (userData.nickname) localStorage.setItem('nickname', userData.nickname);
    if (userData.membership) localStorage.setItem('membership', userData.membership);
    if (userData.profile_image) localStorage.setItem('profile_image', userData.profile_image);
    if (userData.profile_intro) localStorage.setItem('profile_intro', userData.profile_intro);
    if (userData.id) localStorage.setItem('id', userData.id);
    
    // 토큰 저장.
    // 보안(H-2): JS로 auth_token 쿠키를 직접 쓰지 않는다.
    //   - 세션 쿠키는 서버가 내려주는 HttpOnly 쿠키(Set-Cookie)가 유일한 소스이며,
    //     JS로 접근 가능한 쿠키 사본은 HttpOnly 보호를 무력화하고 XSS 노출면을 넓힌다.
    //   - 크로스 오리진(Pages↔Railway) API 인증은 localStorage 토큰을 Authorization: Bearer 로 첨부해 처리한다.
    if (userData.token) {
      localStorage.setItem('auth_token', userData.token);
    }
    
    // 사용자 데이터 변경 이벤트 발생
    window.dispatchEvent(new CustomEvent('userDataChanged'));
  }

  // 사용자 정보 가져오기
  getUserData() {
    return {
      email: localStorage.getItem('email'),
      nickname: localStorage.getItem('nickname'),
      membership: localStorage.getItem('membership'),
      profile_image: localStorage.getItem('profile_image'),
      profile_intro: localStorage.getItem('profile_intro'),
      id: localStorage.getItem('id'),
      token: localStorage.getItem('auth_token')
    };
  }

  // 로그인 상태 확인
  isLoggedIn() {
    return !!localStorage.getItem('auth_token');
  }

  // 토큰 검증
  // @param {{ extend?: boolean }} [options] - extend:true 이면 서버에 강제 세션 연장 요청
  async verifyToken(options = {}) {
    devLog('[authService.verifyToken] 🚀 함수 시작');
    try {
      const qs = options.extend ? '?extend=1' : '';
      const url = this.isDevelopment
        ? `/api/verify-token${qs}`
        : `${this.baseUrl}/api/verify-token${qs}`;
      devLog('[authService.verifyToken] 📍 URL:', url);
      devLog('[authService.verifyToken] 🔧 isDevelopment:', this.isDevelopment);

      // ✅ apiClient 사용: 타임아웃 및 에러 처리 자동 지원
      devLog('[authService.verifyToken] ⏳ getJson 호출 시작 (timeout: 10000ms)');
      const data = await getJson(url, {
        timeout: 10000, // 10초 타임아웃
        retries: 0 // 인증 검증은 재시도하지 않음
      });
      devLog('[authService.verifyToken] ✅ getJson 응답 수신:', data);

      if (data.success) {
        devLog('[authService.verifyToken] ✅ 토큰 검증 성공');
        // Token is valid
        // Optionally update localStorage if backend returns user data
        if (data.user) {
          this.saveUserData(data.user);
          devLog('[authService.verifyToken] 👤 사용자 데이터 저장 완료');
        }

        // Handle token renewal if backend renewed it
        if (data.token_renewed) {
          devLog('[authService.verifyToken] 🔄 토큰이 자동으로 갱신됨');
        }

        const result = { success: true, expires_in: data.expires_in };
        devLog('[authService.verifyToken] ✅ 함수 완료 (성공):', result);
        return result;
      } else {
        devLog('[authService.verifyToken] ❌ 토큰 검증 실패:', data.error);
        // Token is invalid or expired
        this.logout(); // Clear localStorage and cookies
        const result = { success: false, error: data.error || 'Token expired' };
        devLog('[authService.verifyToken] ✅ 함수 완료 (실패):', result);
        return result;
      }
    } catch (error) {
      console.error('[authService.verifyToken] ❌ 에러 발생:', error);
      console.error('[authService.verifyToken] ❌ 에러 타입:', error.constructor.name);
      console.error('[authService.verifyToken] ❌ 에러 메시지:', error.message);
      // ✅ 타입 안전한 에러 처리
      if (error instanceof NetworkError || error instanceof TimeoutError) {
        // Network error - don't logout on network errors
        devLog('[authService.verifyToken] 🌐 네트워크/타임아웃 에러');
        const result = { success: false, error: 'Network error', isNetworkError: true };
        devLog('[authService.verifyToken] ✅ 함수 완료 (네트워크 에러):', result);
        return result;
      } else if (error instanceof AuthenticationError) {
        // Token is invalid or expired
        devLog('[authService.verifyToken] 🔐 인증 에러');
        this.logout();
        const result = { success: false, error: error.message || 'Token expired' };
        devLog('[authService.verifyToken] ✅ 함수 완료 (인증 에러):', result);
        return result;
      }
      // Other errors
      devLog('[authService.verifyToken] ⚠️ 기타 에러');
      const result = { success: false, error: 'Token verification failed' };
      devLog('[authService.verifyToken] ✅ 함수 완료 (기타 에러):', result);
      return result;
    }
  }

  // 로그아웃
  logout() {
    localStorage.removeItem('email');
    localStorage.removeItem('nickname');
    localStorage.removeItem('membership');
    localStorage.removeItem('profile_image');
    localStorage.removeItem('profile_intro');
    localStorage.removeItem('id');
    localStorage.removeItem('auth_token');
    
    try {
      document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    } catch (e) {
      // console.warn('Failed to clear cookie:', e);
    }
    
    // 로그아웃 이벤트 발생
    window.dispatchEvent(new CustomEvent('userDataChanged'));
  }

  // 비밀번호 로그인
  async passwordLogin(identifier, password) {
    try {
      // 개발 환경에서는 프록시를 통해 상대 경로 사용
      const url = this.isDevelopment ? '/api/login-password' : `${this.baseUrl}/api/login-password`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ identifier, password }),
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success && data.require2FA) {
        sessionStorage.setItem('pre_auth_token', data.pre_auth_token);
        return { success: true, require2FA: true };
      }

      if (data.success) {
        this.saveUserData(data);
        return { success: true, data };
      } else {
        return { success: false, message: data.message || '로그인에 실패했습니다.' };
      }
    } catch (error) {
      console.error('Password login error:', error);
      return { success: false, message: '서버와의 통신 중 오류가 발생했습니다.' };
    }
  }

  // OAuth 콜백 처리 (리다이렉트 후)
  async handleOAuthCallback(code, state, provider) {
    if (!code || !state || !provider) {
      return { success: false, message: 'OAuth 파라미터가 누락되었습니다.' };
    }

    // 개발 환경에서만 백엔드로 OAuth 코드 전달
    if (this.isDevelopment) {
      try {
        // 프록시를 통해 백엔드로 OAuth 코드 전달
        const response = await fetch(`/api/oauth/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            code, 
            state, 
            provider 
          }),
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          this.saveUserData(data);
          return { success: true, data };
        } else {
          return { success: false, message: data.message || 'OAuth 로그인에 실패했습니다.' };
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        return { success: false, message: 'OAuth 처리 중 오류가 발생했습니다.' };
      }
    } else {
      // 배포 환경에서는 백엔드가 직접 OAuth를 처리하므로
      // 사용자 정보를 localStorage에서 가져오거나 페이지 새로고침
      return { success: false, message: '배포 환경에서는 백엔드가 OAuth를 직접 처리합니다.' };
    }
  }

  // 현재 환경 확인
  getEnvironment() {
    return this.isDevelopment ? 'development' : 'production';
  }
}

const authService = new AuthService();
export default authService;
